/**
 * <WorkflowStateBadge /> — pending / approved / denied / cancelled
 * pill, color-coded by state.
 *
 * Per DESIGN_SYSTEM.md §6 + §8: a single badge component for every
 * workflow's state column. Future workflow types (loan, eos, time,
 * excuse) inherit the same color scheme via shared state-name
 * conventions:
 *
 *   draft / submitted           → info (blue-ish neutral)
 *   pending_*                   → state-pending (amber)
 *   approved                    → state-approved (green)
 *   denied / rejected           → state-denied (red)
 *   cancelled / withdrawn       → state-paused (grey)
 *
 * Per the M15.9 origin, this primitive supersedes the M3-era
 * components/WorkflowStateBadge.tsx (which used hardcoded slate-*
 * / emerald-* / rose-* classes). That file is kept as a re-export
 * for backward compat; new code imports from components/ui.
 */

import type { ReactNode } from "react";

const STATE_STYLES: Record<string, { bg: string; fg: string }> = {
  draft: { bg: "bg-surface-sunken", fg: "text-text-secondary" },
  submitted: { bg: "bg-state-info-bg", fg: "text-state-info" },
  pending_approval: {
    bg: "bg-state-pending-bg",
    fg: "text-state-pending",
  },
  pending_decision: {
    bg: "bg-state-pending-bg",
    fg: "text-state-pending",
  },
  pending_review: {
    bg: "bg-state-pending-bg",
    fg: "text-state-pending",
  },
  eligibility_check: { bg: "bg-state-info-bg", fg: "text-state-info" },
  approved: { bg: "bg-state-approved-bg", fg: "text-state-approved" },
  disbursed: { bg: "bg-state-approved-bg", fg: "text-state-approved" },
  in_repayment: { bg: "bg-state-info-bg", fg: "text-state-info" },
  settled: { bg: "bg-state-approved-bg", fg: "text-state-approved" },
  deducted: { bg: "bg-state-approved-bg", fg: "text-state-approved" },
  denied: { bg: "bg-state-denied-bg", fg: "text-state-denied" },
  rejected: { bg: "bg-state-denied-bg", fg: "text-state-denied" },
  cancelled: { bg: "bg-state-paused-bg", fg: "text-state-paused" },
  withdrawn: { bg: "bg-state-paused-bg", fg: "text-state-paused" },
  skipped: { bg: "bg-state-pending-bg", fg: "text-state-pending" },
};

const DEFAULT_TONE = {
  bg: "bg-surface-sunken",
  fg: "text-text-secondary",
};

const PENDING_TONE = {
  bg: "bg-state-pending-bg",
  fg: "text-state-pending",
};

export interface WorkflowStateBadgeProps {
  state: string;
  className?: string;
}

export function WorkflowStateBadge({
  state,
  className = "",
}: WorkflowStateBadgeProps): ReactNode {
  const tone =
    STATE_STYLES[state] ??
    (state.includes("pending") ? PENDING_TONE : DEFAULT_TONE);

  return (
    <span
      className={[
        "inline-flex items-center",
        "rounded-full px-2 py-0.5",
        "text-xs font-medium",
        tone.bg,
        tone.fg,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {state.replace(/_/g, " ")}
    </span>
  );
}
